// Path: src/Screens/admin/MaintenanceRequest/MaintenanceRequestManagement.jsx

import React, { useState, useMemo, useEffect, useContext } from 'react';
import AdminMasterLayout from '../../../layout/superadmin/MasterLayout';
import { FiMoreVertical, FiTrash2, FiEye, FiEdit } from 'react-icons/fi';
import { GiFarmTractor } from 'react-icons/gi';
import { MdOutlinePendingActions, MdAutorenew, MdCheckCircleOutline } from 'react-icons/md';
import Swal from 'sweetalert2';
import AppPagination from "../../../components/general/pagination/Pagination.js";
import '../../../assets/css/superadmin/maintenancerequest/MaintenceRequest.css';
import AppContext from '../../../context/AppContext.js';
import axios from 'axios';

// ─── Skeleton Components ──────────────────────────────────────────────────────

const StatCardSkeleton = () => (
  <div className="mrm-stat-card mrm-stat-card--skeleton">
    <div className="mrm-skl mrm-skl-icon" />
    <div className="mrm-stat-content">
      <div className="mrm-skl mrm-skl-value" />
      <div className="mrm-skl mrm-skl-label" />
    </div>
  </div>
);

const TableRowSkeleton = () => (
  <tr className="mrm-skeleton-row">
    <td className="mrm-table-td"><div className="mrm-skl mrm-skl-name" /></td>
    <td className="mrm-table-td"><div className="mrm-skl mrm-skl-machinery" /></td>
    <td className="mrm-table-td"><div className="mrm-skl mrm-skl-date" /></td>
    <td className="mrm-table-td"><div className="mrm-skl mrm-skl-issue" /></td>
    <td className="mrm-table-td"><div className="mrm-skl mrm-skl-location" /></td>
    <td className="mrm-table-td"><div className="mrm-skl mrm-skl-badge" /></td>
    <td className="mrm-table-td mrm-text-center"><div className="mrm-skl mrm-skl-btn" /></td>
  </tr>
);

// ─── Main Component ───────────────────────────────────────────────────────────

const MaintenanceRequest = () => {
  const { baseUrl, baseUrlForFile ,authToken} = useContext(AppContext);
  const [isLoading, setIsLoading] = useState(true);
  const [activeDropdown, setActiveDropdown] = useState(null);
  const [activeFilter, setActiveFilter] = useState('pending');
  const [currentPage, setCurrentPage] = useState(1);
  const [requests, setRequests] = useState([]);
  const requestsPerPage = 5;


  function getStatusLabel(status) {
    const s = (status || '').toLowerCase();
    if (['active', 'in-progress', 'inprogress'].includes(s)) return 'In Progress';
    if (s === 'completed') return 'Completed';
    return 'Pending';
  }


  useEffect(() => {
    const timer = setTimeout(() => setIsLoading(false), 1800);
    return () => clearTimeout(timer);
  }, []);

  const fetchAllMechanicBooking = () => {
    axios.get(`${baseUrl}/superadmin/mechanicBookings/fetchAllMechanicBooking`, {
      headers: { Authorization: `Bearer ${authToken}` }
    }
    )
      .then((res) => {
        console.log(res.data);
        if (res.data.success == true) {
          setRequests(res.data.mechanicrequests);
        }
      })
      .catch((err) => { console.log(err); });
  };

  useEffect(() => {
    fetchAllMechanicBooking();
  }, []);

  const stats = useMemo(() => ({
    total: requests.length,
    pending: requests.filter(r => (r.status || '') === 'pending').length,
    inProgress: requests.filter(r => ['active', 'in-progress', 'inprogress'].includes((r.status || '').toLowerCase())).length,
    completed: requests.filter(r => (r.status || '') === 'completed').length,
  }), [requests]);

  const filteredRequests = useMemo(() => {
    if (activeFilter === 'all') return requests;
    if (activeFilter === 'in-progress') {
      return requests.filter(r =>
        ['active', 'in-progress', 'inprogress'].includes((r.status || '').toLowerCase())
      );
    }
    return requests.filter(r => (r.status || '').toLowerCase() === activeFilter);
  }, [requests, activeFilter]);

  const totalPages = Math.ceil(filteredRequests.length / requestsPerPage);
  const paginatedRequests = filteredRequests.slice(
    (currentPage - 1) * requestsPerPage,
    currentPage * requestsPerPage
  );

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (!e.target.closest('.mrm-dropdown-wrapper')) setActiveDropdown(null);
    };
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, []);

  const toggleDropdown = (e, id) => {
    e.stopPropagation();
    setActiveDropdown(activeDropdown === id ? null : id);
  };

  // ✅ NEW: "Booking Detail" style modal — black header + close X + info boxes
  const handleViewDetails = (request) => {
    const mechanicName = request.srmechanicadmin?.fullname || 'N/A';
    const mechanicSpecialization = request.srmechanicadmin?.specialization || "N/A";
    const customerName = request.srcustomer?.fullname || request.srrentaladmin?.fullname || 'N/A';
    const statusLabel = getStatusLabel(request.status);
    const statusClass = getStatusClass(request.status);

    Swal.fire({
      html: `
        <div class="mrm-detail-header">
          <h2>Maintenance Request Detail</h2>
          <p>Full summary and request information</p>
        </div>
        <div class="mrm-detail-topbar">
          <span class="mrm-detail-id">#${request.id}</span>
          <span class="mrm-status-badge ${statusClass}">${statusLabel}</span>
          <span class="mrm-detail-date">${request.preferreddate || '—'}</span>
        </div>
        <div class="mrm-detail-section">
          <h4>People</h4>
          <div class="mrm-detail-grid">
            <div class="mrm-detail-box">
              <span>Mechanic</span>
              <strong>${mechanicName}</strong>
            </div>
            <div class="mrm-detail-box">
            <span>Specialization</span>
            <strong>${mechanicSpecialization}</strong>
        </div>
            <div class="mrm-detail-box">
              <span>Customer</span>
              <strong>${customerName}</strong>
            </div>
          </div>
        </div>
        <div class="mrm-detail-section">
          <h4>Job Details</h4>
          <div class="mrm-detail-grid">
            <div class="mrm-detail-box">
              <span>Machinery Type</span>
              <strong>${request.machinerytype || '—'}</strong>
            </div>
            <div class="mrm-detail-box">
              <span>Location</span>
              <strong>${request.location || '—'}</strong>
            </div>
          </div>
          <div class="mrm-detail-box mrm-detail-box-full">
            <span>Issue Description</span>
            <strong>${request.descriptionissue || '—'}</strong>
          </div>
        </div>
      `,
      showConfirmButton: true,
      confirmButtonText: 'Close',
      confirmButtonColor: '#FDB714',
      showCloseButton: true,
      width: '560px',
      padding: 0,
      customClass: { popup: 'mrm-detail-popup' }
    });
    setActiveDropdown(null);
  };

  const handleStatusUpdate = async (request) => {
    const statusOptions = {
      'pending': 'Pending',
      'in-progress': 'In Progress',
      'completed': 'Completed',
    };

    const statusRadios = Object.entries(statusOptions).map(([value, label]) =>
      `<div style="text-align: left; margin: 10px 0;">
        <label style="cursor: pointer; display: flex; align-items: center; gap: 10px;">
          <input type="radio" name="status" value="${value}"
            ${(request.status || '') === value ? 'checked' : ''}
            style="width: 18px; height: 18px; cursor: pointer;">
          <span style="font-size: 15px; font-family: 'Poppins', sans-serif;">${label}</span>
        </label>
      </div>`
    ).join('');

    const { value: formValues } = await Swal.fire({
      title: '<strong>Update Status</strong>',
      html: `
        <div style="padding: 10px;">
          <p style="font-family: 'Poppins', sans-serif; color: #666; margin-bottom: 15px;">
            Update status for <strong>${request.fullname || '—'}</strong>
          </p>
          <div style="background: #f9f9f9; padding: 15px; border-radius: 8px;">
            <label style="display: block; font-family: 'Poppins', sans-serif; font-weight: 600; margin-bottom: 10px; color: #000;">
              Status:
            </label>
            ${statusRadios}
          </div>
        </div>
      `,
      focusConfirm: false,
      showCancelButton: true,
      confirmButtonText: 'Update',
      cancelButtonText: 'Cancel',
      confirmButtonColor: '#FDB714',
      cancelButtonColor: '#999',
      width: '550px',
      preConfirm: () => {
        const selectedStatus = document.querySelector('input[name="status"]:checked')?.value;
        if (!selectedStatus) { Swal.showValidationMessage('Please select a status'); return false; }
        return { status: selectedStatus };
      }
    });

    if (formValues) {
      setRequests(prev => prev.map(req =>
        req.id === request.id ? { ...req, status: formValues.status } : req
      ));
      Swal.fire({ title: 'Updated!', text: 'Request status has been updated successfully.', icon: 'success', confirmButtonColor: '#FDB714' });
    }
    setActiveDropdown(null);
  };

  const handleDelete = (request) => {
    Swal.fire({
      title: 'Delete Request?',
      text: `Are you sure you want to delete the request from ${request.fullname || '—'}?`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#DC3545',
      cancelButtonColor: '#999',
      confirmButtonText: 'Yes, delete it',
      cancelButtonText: 'Cancel',
    }).then((result) => {
      if (result.isConfirmed) {
        setRequests(prev => prev.filter(req => req.id !== request.id));
        Swal.fire({ title: 'Deleted!', text: 'Request has been deleted successfully.', icon: 'success', confirmButtonColor: '#FDB714' });
      }
    });
    setActiveDropdown(null);
  };

  const getStatusClass = (status) => {
    const s = (status || '').toLowerCase();
    if (s === 'pending') return 'mrm-status-pending';
    if (['active', 'in-progress', 'inprogress'].includes(s)) return 'mrm-status-progress';
    if (s === 'completed') return 'mrm-status-completed';
    return 'mrm-status-pending';
  };

  return (
    <AdminMasterLayout>
      <div className="mrm-shell">
        <div className="mrm-container">

          <div className="mrm-page-header">
            <div>
              <h1 className="mrm-page-title">Maintenance Requests</h1>
              <p className="mrm-page-subtitle">Manage and assign maintenance jobs</p>
            </div>
          </div>

          <div className="mrm-stats-grid">
            {isLoading ? (
              <><StatCardSkeleton /><StatCardSkeleton /><StatCardSkeleton /><StatCardSkeleton /></>
            ) : (
              <>
                <div className="mrm-stat-card mrm-stat-total">
                  <div className="mrm-stat-icon"><GiFarmTractor /></div>
                  <div className="mrm-stat-content"><h3 className="mrm-stat-value">{stats.total}</h3><p className="mrm-stat-label">Total Requests</p></div>
                </div>
                <div className="mrm-stat-card mrm-stat-pending">
                  <div className="mrm-stat-icon"><MdOutlinePendingActions /></div>
                  <div className="mrm-stat-content"><h3 className="mrm-stat-value">{stats.pending}</h3><p className="mrm-stat-label">Pending</p></div>
                </div>
                <div className="mrm-stat-card mrm-stat-progress">
                  <div className="mrm-stat-icon"><MdAutorenew /></div>
                  <div className="mrm-stat-content"><h3 className="mrm-stat-value">{stats.inProgress}</h3><p className="mrm-stat-label">In Progress</p></div>
                </div>
                <div className="mrm-stat-card mrm-stat-completed">
                  <div className="mrm-stat-icon"><MdCheckCircleOutline /></div>
                  <div className="mrm-stat-content"><h3 className="mrm-stat-value">{stats.completed}</h3><p className="mrm-stat-label">Completed</p></div>
                </div>
              </>
            )}
          </div>

          <div className="mrm-filter-tabs">
            {isLoading ? (
              <><div className="mrm-skl mrm-skl-tab" /><div className="mrm-skl mrm-skl-tab" /><div className="mrm-skl mrm-skl-tab" /></>
            ) : (
              <>
                <button className={`mrm-filter-tab ${activeFilter === 'pending' ? 'mrm-filter-tab-active' : ''}`} onClick={() => { setActiveFilter('pending'); setCurrentPage(1); }}>
                  Pending ({stats.pending})
                </button>
                <button className={`mrm-filter-tab ${activeFilter === 'in-progress' ? 'mrm-filter-tab-active' : ''}`} onClick={() => { setActiveFilter('in-progress'); setCurrentPage(1); }}>
                  In Progress ({stats.inProgress})
                </button>
                <button className={`mrm-filter-tab ${activeFilter === 'completed' ? 'mrm-filter-tab-active' : ''}`} onClick={() => { setActiveFilter('completed'); setCurrentPage(1); }}>
                  Completed ({stats.completed})
                </button>
              </>
            )}
          </div>

          <div className="mrm-table-card">
            <div className="mrm-table-header">
              {isLoading
                ? <div className="mrm-skl mrm-skl-title" />
                : <h2 className="mrm-table-title">
                  {activeFilter === 'pending' ? 'Pending Requests' :
                    activeFilter === 'in-progress' ? 'In Progress Requests' :
                      'Completed Requests'}
                </h2>
              }
            </div>

            <div className="mrm-table-wrapper">
              <table className="mrm-table">
                <thead className="mrm-table-head">
                  <tr>
                    <th className="mrm-table-th">MECHANIC</th>
                    <th className="mrm-table-th">CUSTOMER</th>
                    <th className="mrm-table-th">MACHINERY TYPE</th>
                    <th className="mrm-table-th">PREFERRED DATE</th>
                    <th className="mrm-table-th">ISSUE</th>
                    <th className="mrm-table-th">STATUS</th>
                    <th className="mrm-table-th mrm-text-center">ACTIONS</th>
                  </tr>
                </thead>
                <tbody className="mrm-table-body">
                  {isLoading ? (
                    Array.from({ length: 5 }).map((_, i) => <TableRowSkeleton key={i} />)
                  ) : paginatedRequests.length > 0 ? (
                    paginatedRequests.map((request) => (
                      <tr key={request.id} className="mrm-table-row">

                        <td className="mrm-table-td">
                          <div className="mrm-name">
                            <img
                              src={`${baseUrlForFile}/${request.srmechanicadmin?.image}`}
                              alt="Mechanic"
                              className="mrm-avatar-img"
                            />
                            <div>
                              <div className="mrm-person-name">{request.srmechanicadmin?.fullname || "N/A"}</div>
                              <div className="mrm-person-email">{request.srmechanicadmin?.email}</div>
                            </div>
                          </div>
                        </td>
                        <td className="mrm-table-td">
                          <div className="mrm-name">
                            <img
                              src={`${baseUrlForFile}/${request.srcustomer?.image || request.srrentaladmin?.image}`}
                              alt="Customer"
                              className="mrm-avatar-img"
                            />
                            <div>
                              <div className="mrm-person-name">
                                {request.srcustomer?.fullname || request.srrentaladmin?.fullname || "N/A"}
                              </div>
                              <div className="mrm-person-email">
                                {request.srcustomer?.email || request.srrentaladmin?.email || "N/A"}
                              </div>
                            </div>
                          </div>
                        </td>

                        <td className="mrm-table-td">
                          <div className="mrm-machinery-cell">
                            <GiFarmTractor className="mrm-machinery-icon" />
                            <span>{request.machinerytype || '—'}</span>
                          </div>
                        </td>

                        <td className="mrm-table-td">
                          <span className="mrm-date">{request.preferreddate || '—'}</span>
                        </td>

                        <td className="mrm-table-td">
                          <span className="mrm-issue-text">
                            {(request.descriptionissue || '—').length > 30
                              ? (request.descriptionissue).substring(0, 30) + '...'
                              : (request.descriptionissue || '—')}
                          </span>
                        </td>

                        <td className="mrm-table-td">
                          <span className={`mrm-status-badge ${getStatusClass(request.status)}`}>
                            {['active', 'in-progress', 'inprogress'].includes((request.status || '').toLowerCase())
                              ? <><MdAutorenew /> In Progress</>
                              : request.status === 'completed'
                                ? <><MdCheckCircleOutline /> Completed</>
                                : <><MdOutlinePendingActions /> Pending</>
                            }
                          </span>
                        </td>

                        <td className="mrm-table-td mrm-text-center">
                          <div className="mrm-dropdown-wrapper">
                            <button className="mrm-dropdown-toggle" onClick={(e) => toggleDropdown(e, request.id)}>
                              <FiMoreVertical size={18} />
                            </button>
                            {activeDropdown === request.id && (
                              <div className="mrm-dropdown-menu">
                                <button className="mrm-dropdown-item" onClick={() => handleViewDetails(request)}>
                                  <FiEye size={16} /> View Details
                                </button>
                                <div className="mrm-dropdown-divider" />
                              </div>
                            )}
                          </div>
                        </td>

                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="8" className="mrm-no-data">
                        <div className="mrm-empty-state">
                          <GiFarmTractor size={48} color="#ccc" />
                          <p>No requests found</p>
                        </div>
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            {!isLoading && (
              <AppPagination
                currentPage={currentPage}
                totalPages={totalPages}
                totalItems={filteredRequests.length}
                itemsPerPage={requestsPerPage}
                onPageChange={(page) => setCurrentPage(page)}
              />
            )}
          </div>

        </div>
      </div>
    </AdminMasterLayout>
  );
};


export default MaintenanceRequest;